import React, { useState } from 'react';
import type { HyroxPortSheetData } from '../lib/api';

interface PenaltyCardProps {
  penalty: HyroxPortSheetData;
  onApprove: (penlogid: number) => Promise<void>;
  onReject: (penlogid: number) => Promise<void>;
  onPhotoClick: (penlogid: number) => void;
}

function formatTimestamp(dateStr: string): string {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
}

/** Strip leading station number prefix, e.g. "4 BBJ" → "BBJ", "06 Farmers Carry" → "Farmers Carry" */
function clipStationName(station: string | null | undefined): string {
  if (!station) return '—';
  const clipped = station.trim().replace(/^\d+[.\-:_]?\s*/, '');
  return clipped || station.trim() || '—';
}

function getJudgeLabel(judgename: string, approvedAt: string): string {
  const prefix = judgename?.split('@')[0] || 'Judge';
  const time = formatTimestamp(approvedAt);
  return `${prefix} — ${time || 'Unknown'}`;
}

function barClass(status: string): string {
  if (status === 'Needs Review') return 'bg-sw-red';
  if (status === 'Warning') return 'bg-sw-orange';
  if (status === 'Approved') return 'bg-sw-green';
  if (status === 'Rejected') return 'bg-sw-red';
  return 'bg-sw-yellow';
}

function tagClass(status: string): string {
  if (status === 'Needs Review') return 'bg-sw-red text-white';
  if (status === 'Warning') return 'bg-sw-orange text-black';
  if (status === 'Approved') return 'bg-sw-green text-white';
  if (status === 'Rejected') return 'bg-sw-red text-white';
  return 'bg-black text-white';
}

export const PenaltyCard: React.FC<PenaltyCardProps> = ({ penalty, onApprove, onReject, onPhotoClick }) => {
  const [resolvedAction, setResolvedAction] = useState<'approved' | 'rejected' | null>(null);
  const [isActioning, setIsActioning] = useState(false);

  const currentStatus = resolvedAction
    ? (resolvedAction === 'approved' ? 'Approved' : 'Rejected')
    : penalty.status;

  const handleApprove = async () => {
    setIsActioning(true);
    try {
      await onApprove(penalty.penlogid);
      setResolvedAction('approved');
    } finally {
      setIsActioning(false);
    }
  };

  const handleReject = async () => {
    setIsActioning(true);
    try {
      await onReject(penalty.penlogid);
      setResolvedAction('rejected');
    } finally {
      setIsActioning(false);
    }
  };

  const showActions = !resolvedAction && penalty.status === 'Needs Review';
  const typeLabel =
    penalty.penaltytype === '15-Sec' || penalty.penaltytype === '30-Sec'
      ? 'Penalty'
      : (penalty.penaltytype || 'Penalty');
  const penaltyTimeNum = Number(penalty.penaltytime);
  const isHoldBonus = penaltyTimeNum < 0 || /hold|bonus/i.test(String(penalty.penaltytype || ''));
  const timeDisplay = isHoldBonus
    ? String(penalty.penaltytime)
    : `+${penalty.penaltytime}`;

  return (
    <div className="w-full max-w-[420px] flex border-2 border-black bg-white overflow-hidden font-brutal shadow-[0_8px_24px_rgba(0,0,0,0.35)]">
      {/* Status accent bar — vertical left edge (wireframe .qrow .bar) */}
      <div className={`w-3.5 flex-shrink-0 self-stretch ${barClass(currentStatus)}`} aria-hidden />

      <div className="flex-1 min-w-0 flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between gap-2 px-3.5 py-3 bg-[#e6e6e6] border-b-2 border-black">
          <div className="min-w-0">
            <div className="uppercase tracking-[0.12em] text-[10px] font-extrabold text-[#6b6b6b] truncate font-brutal">
              {getJudgeLabel(penalty.judgename, penalty.approved_at)}
            </div>
            <div className="text-[20px] font-black tracking-[0.02em] text-black uppercase truncate leading-tight font-brutal">
              {typeLabel}
            </div>
          </div>
          <span
            className={`flex-shrink-0 text-[11px] font-black uppercase tracking-[0.04em] px-2 py-1 font-brutal ${tagClass(currentStatus)}`}
          >
            {currentStatus}
          </span>
        </div>

        {/* Body */}
        <div className="bg-[#a6a6a6]">
          <div className="px-3.5 py-3" >
            {/* Row 1: Time + Station */}
            <div className="flex items-center gap-3.5 mb-2.5">
              <div className="flex items-baseline gap-1.5 min-w-0" style={{ flex: '1 1 0%' }}>
                <span className="font-mono font-light text-[24px] leading-none text-black tracking-tight">
                  {timeDisplay}
                </span>
                <span className="text-[11px] font-semibold uppercase tracking-[0.03em] text-[#6b6b6b] font-brutal">
                  sec
                </span>
              </div>

              <div className="w-[2.5px] self-stretch bg-black flex-shrink-0" />

              <div className="min-w-0" style={{ flex: '2 1 0%' }}>
                <div className="text-[12px] font-extrabold uppercase tracking-[0.03em] text-black truncate leading-tight font-brutal">
                  {clipStationName(penalty.station)}
                </div>
              </div>
            </div>

            <div className="border-t-2 border-black/10 my-1" />

            {/* Row 2: Athlete + Photo */}
            <div className="flex items-center gap-3 pt-2">
              <div className="flex-1 min-w-0">
                <div className="font-mono font-light text-[28px] leading-none text-black tracking-tight truncate">
                  {String(penalty.racenumber ?? '').replace(/^#/, '') || '—'}
                </div>
              </div>
              <div className="flex flex-col items-center flex-shrink-0">
                <button
                  type="button"
                  onClick={() => penalty.hasimage && onPhotoClick(penalty.penlogid)}
                  disabled={!penalty.hasimage}
                  className="w-11 h-11 flex items-center justify-center border-2 border-black transition-colors disabled:opacity-40"
                  style={{
                    background: penalty.hasimage ? 'rgba(255,237,0,0.35)' : '#f4f4f1',
                    cursor: penalty.hasimage ? 'pointer' : 'default',
                  }}
                  title={penalty.hasimage ? 'View photo' : 'No photo'}
                >
                  <svg viewBox="0 0 20 20" fill="none" className="w-[18px] h-[18px]">
                    <rect
                      x="2"
                      y="4.5"
                      width="16"
                      height="11"
                      rx="0"
                      stroke="#0b0b0c"
                      strokeWidth="1.2"
                    />
                    <circle
                      cx="10"
                      cy="10"
                      r="3"
                      stroke="#0b0b0c"
                      strokeWidth="1.2"
                    />
                    <circle cx="10" cy="10" r="1.2" fill="#0b0b0c" />
                  </svg>
                </button>
                <span className="text-[9px] font-extrabold mt-1 tracking-[0.06em] uppercase text-[#6b6b6b] font-brutal">
                  {penalty.hasimage ? '1 Photo ↗' : 'No Photo'}
                </span>
              </div>
            </div>
          </div>

          {/* Resolved banner */}
          {resolvedAction && (
            <div
              className={`mx-3.5 mb-3 px-3 py-2 text-center text-xs font-extrabold uppercase tracking-wide font-brutal ${
                resolvedAction === 'approved' ? 'pen-success !rounded-none' : 'pen-error !rounded-none'
              }`}
            >
              {resolvedAction === 'approved' ? '✔ Penalty Approved' : '✖ Penalty Rejected'}
            </div>
          )}

          {/* Action buttons — square corners like wireframe .racts / .qact */}
          {showActions && (
            <div className="flex gap-2.5 px-3.5 pb-3.5">
              <button
                type="button"
                onClick={handleReject}
                disabled={isActioning}
                className="flex-1 min-h-10 py-2.5 px-3 text-[13px] font-black uppercase tracking-[0.03em] font-brutal bg-[#e6e6e6] text-sw-red border-2 border-sw-red disabled:opacity-35 disabled:pointer-events-none"
              >
                Reject
              </button>
              <button
                type="button"
                onClick={handleApprove}
                disabled={isActioning}
                className="flex-1 min-h-10 py-2.5 px-3 text-[13px] font-black uppercase tracking-[0.03em] font-brutal bg-sw-green text-white border-2 border-black disabled:opacity-35 disabled:pointer-events-none"
              >
                Approve
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
